export type ImportSessionStatus = 'created' | 'uploading' | 'processing' | 'ready_for_matching' | 'matching_in_progress' | 'ready_for_review' | 'importing' | 'completed' | 'failed' | 'cancelled';
export type PatientImportStatus = 'extracted' | 'matching' | 'new' | 'matched' | 'imported' | 'merged' | 'skipped' | 'failed';

/**
 * Get the progress phase description based on status and actual patient counts
 */
export const getProgressPhaseDescription = (status: string, percentage?: number, processed?: number, total?: number): string => {
  const statusLower = status?.toLowerCase();

  // Prefer actual patient counts over percentage when available
  const usePatientCounts = processed !== undefined && total !== undefined && total > 0 && total <= 1000; // Sanity check to avoid percentage confusion

  switch (statusLower) {
    case 'created':
      return 'Session created, waiting for upload...';
    case 'uploading':
      return 'Uploading file...';
    case 'processing':
      if (usePatientCounts) {
        return `Extracting patient data (${processed} of ${total} patients)`;
      }
      return percentage !== undefined ? `Extracting patient data (${percentage}%)` : 'Extracting patient data from files...';
    case 'ready_for_matching':
      return 'Ready to start duplicate detection';
    case 'matching_in_progress':
      if (usePatientCounts) {
        return `Running duplicate detection (${processed} of ${total} patients)`;
      }
      return percentage !== undefined ? `Running duplicate detection (${percentage}%)` : 'Running duplicate detection...';
    case 'ready_for_review':
      return 'Ready for admin review and import decisions';
    case 'importing':
      if (usePatientCounts) {
        return `Importing selected patients (${processed} of ${total} patients)`;
      }
      return percentage !== undefined ? `Importing selected patients (${percentage}%)` : 'Importing selected patients...';
    case 'completed':
      return 'Processing completed successfully';
    case 'failed':
      return 'Processing failed';
    case 'cancelled':
      return 'Session cancelled';
    default:
      return 'Processing...';
  }
};

/**
 * Get the status label for display
 */
export const getStatusLabel = (status: string, progressPercentage?: number): string => {
  const statusLower = status?.toLowerCase();

  switch (statusLower) {
    case 'created':
      return 'Created';
    case 'uploading':
      return 'Uploading';
    case 'processing':
      return progressPercentage !== undefined ? `Extracting Data (${progressPercentage}%)` : 'Extracting Data';
    case 'ready_for_matching':
      return 'Ready for Matching';
    case 'matching_in_progress':
      return progressPercentage !== undefined ? `Matching Patients (${progressPercentage}%)` : 'Matching Patients';
    case 'ready_for_review':
      return 'Ready for Review';
    case 'importing':
      return progressPercentage !== undefined ? `Importing Patients (${progressPercentage}%)` : 'Importing Patients';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status || 'Unknown';
  }
};

/**
 * Get the status label for individual patient imports
 */
export const getPatientStatusLabel = (status: string): string => {
  const statusLower = status?.toLowerCase();

  switch (statusLower) {
    case 'extracted':
      return 'Extracted from File';
    case 'matching':
      return 'Running Duplicate Detection';
    case 'new':
      return 'Ready for Import (New Patient)';
    case 'matched':
      return 'Potential Duplicates Found';
    case 'imported':
      return 'Successfully Imported';
    case 'merged':
      return 'Merged with Existing';
    case 'skipped':
      return 'Skipped by Admin';
    case 'failed':
      return 'Processing Failed';
    default:
      return status || 'Unknown';
  }
};

/**
 * Get the short status label for compact displays
 */
export const getShortStatusLabel = (status: string): string => {
  const statusLower = status?.toLowerCase();

  switch (statusLower) {
    case 'created':
      return 'Created';
    case 'uploading':
      return 'Uploading';
    case 'processing':
      return 'Extracting';
    case 'ready_for_matching':
      return 'Ready to Match';
    case 'matching_in_progress':
      return 'Matching';
    case 'ready_for_review':
      return 'Ready for Review';
    case 'importing':
      return 'Importing';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status || 'Unknown';
  }
};

/**
 * Get the appropriate color for the status chip
 */
export const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const statusLower = status?.toLowerCase();

  switch (statusLower) {
    case 'created':
      return 'secondary';
    case 'uploading':
      return 'warning';
    case 'processing':
    case 'matching_in_progress':
    case 'importing':
      return 'primary';
    case 'ready_for_matching':
    case 'ready_for_review':
      return 'info';
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'secondary';
    default:
      return 'default';
  }
};

/**
 * Get the appropriate icon name for the status
 * Note: The actual icon components need to be imported and used in the React components
 */
export const getStatusIconName = (status: string): string | undefined => {
  const statusLower = status?.toLowerCase();

  switch (statusLower) {
    case 'created':
      return 'AddIcon';
    case 'uploading':
      return 'CloudUploadIcon';
    case 'processing':
    case 'matching_in_progress':
    case 'importing':
      return 'CircularProgress';
    case 'ready_for_matching':
      return 'FindInPageIcon';
    case 'ready_for_review':
      return 'ReviewIcon';
    case 'completed':
      return 'CheckCircleIcon';
    case 'failed':
      return 'ErrorIcon';
    case 'cancelled':
      return 'CancelIcon';
    default:
      return undefined;
  }
};

/**
 * Determine if the session is actively processing
 */
export const isSessionProcessing = (status: string): boolean => {
  const statusLower = status?.toLowerCase();
  return statusLower === 'uploading' || statusLower === 'processing' || statusLower === 'matching_in_progress' || statusLower === 'importing';
};

/**
 * Get the appropriate polling interval based on status
 */
export const getPollingInterval = (status: string): number | false => {
  const statusLower = status?.toLowerCase();

  switch (statusLower) {
    case 'pending':
      return 30000; // 30 seconds for queued items
    case 'extracting':
    case 'matching':
    case 'importing':
      return 3000; // 3 seconds for active processing
    case 'ready_for_review':
      return false; // No polling - waiting for admin action
    case 'completed':
    case 'failed':
    case 'cancelled':
      return false; // No polling for terminal states
    default:
      return 30000; // Default to 30 seconds
  }
};

/**
 * Format patient statistics for display
 */
export const formatPatientStats = (
  total: number,
  processed: number,
  failed: number,
  newPatients: number
): string => {
  const parts: string[] = [];

  if (total > 0) {
    parts.push(`${total} total`);
  }

  if (processed > 0) {
    parts.push(`${processed} processed`);
  }

  if (newPatients > 0) {
    parts.push(`${newPatients} new`);
  }

  if (failed > 0) {
    parts.push(`${failed} failed`);
  }

  return parts.join(' • ');
};

/**
 * Calculate estimated time remaining based on progress
 */
export const getEstimatedTimeRemaining = (
  progressPercentage: number,
  startTime: string
): string | null => {
  if (progressPercentage <= 0 || progressPercentage >= 100) {
    return null;
  }

  const now = new Date();
  const start = new Date(startTime);
  const elapsedMs = now.getTime() - start.getTime();

  // Calculate estimated total time
  const estimatedTotalMs = (elapsedMs / progressPercentage) * 100;
  const remainingMs = estimatedTotalMs - elapsedMs;

  // Convert to human-readable format
  const remainingSeconds = Math.floor(remainingMs / 1000);

  if (remainingSeconds < 60) {
    return `${remainingSeconds}s remaining`;
  }

  const remainingMinutes = Math.floor(remainingSeconds / 60);
  if (remainingMinutes < 60) {
    return `${remainingMinutes}m remaining`;
  }

  const remainingHours = Math.floor(remainingMinutes / 60);
  return `${remainingHours}h ${remainingMinutes % 60}m remaining`;
};